/**
 * La cuenta de los socios, separada de la UI.
 *
 * Neto de un socio = retiros en plata + retiros en mercaderia − aportes. El
 * sueldo va aparte y NO entra: es lo pactado, un gasto del local, y no dice
 * quien se llevo de mas (0108). Se compara en DOLARES: en pesos, un retiro de
 * marzo y uno de septiembre no son comparables. Los pesos se suman igual, como
 * dato al costado.
 *
 * Se suma en el navegador porque `GET /socios/movimientos` trae el periodo
 * entero, no una pagina.
 */
import { redondear } from "@tora/shared";

import { aNumero, type ClaseMovimientoSocio, type MovimientoSocio, type Socio } from "@/lib/tipos";

export interface ResumenSocio {
  socio: Socio;
  plataUsd: number;
  mercaderiaUsd: number;
  aportesUsd: number;
  /** Aparte: no suma al neto. */
  sueldoUsd: number;
  netoUsd: number;
  plataArs: number;
  mercaderiaArs: number;
  aportesArs: number;
  sueldoArs: number;
  netoArs: number;
  cantidad: number;
}

export const ETIQUETA_CLASE: Record<ClaseMovimientoSocio, string> = {
  RETIRO_PLATA: "Retiro en plata",
  RETIRO_MERCADERIA: "Retiro en mercadería",
  APORTE: "Aporte",
  SUELDO: "Sueldo",
};

/**
 * Una fila por socio, en el orden de `socios`, aunque no tenga movimientos: un
 * socio que no aparece en la pantalla parece un socio que no existe.
 *
 * Los movimientos sin socio no se le suman a nadie; la pantalla los muestra
 * aparte para asignarlos.
 */
export function resumirSocios(
  socios: ReadonlyArray<Socio>,
  movimientos: ReadonlyArray<MovimientoSocio>,
): ResumenSocio[] {
  return socios.map((socio) => {
    let plataUsd = 0;
    let mercaderiaUsd = 0;
    let aportesUsd = 0;
    let plataArs = 0;
    let mercaderiaArs = 0;
    let aportesArs = 0;
    let sueldoUsd = 0;
    let sueldoArs = 0;
    let cantidad = 0;

    for (const m of movimientos) {
      if (m.socio_id !== socio.id) continue;
      cantidad++;
      const usd = aNumero(m.importe_usd);
      const ars = aNumero(m.importe_ars);
      if (m.clase === "RETIRO_PLATA") {
        plataUsd += usd;
        plataArs += ars;
      } else if (m.clase === "RETIRO_MERCADERIA") {
        mercaderiaUsd += usd;
        mercaderiaArs += ars;
      } else if (m.clase === "SUELDO") {
        sueldoUsd += usd;
        sueldoArs += ars;
      } else {
        aportesUsd += usd;
        aportesArs += ars;
      }
    }

    return {
      socio,
      plataUsd: redondear(plataUsd),
      mercaderiaUsd: redondear(mercaderiaUsd),
      aportesUsd: redondear(aportesUsd),
      sueldoUsd: redondear(sueldoUsd),
      netoUsd: redondear(plataUsd + mercaderiaUsd - aportesUsd),
      plataArs: redondear(plataArs),
      mercaderiaArs: redondear(mercaderiaArs),
      aportesArs: redondear(aportesArs),
      sueldoArs: redondear(sueldoArs),
      netoArs: redondear(plataArs + mercaderiaArs - aportesArs),
      cantidad,
    };
  });
}

export interface DiferenciaSocios {
  mayor: ResumenSocio;
  menor: ResumenSocio;
  /** Cuanto mas saco `mayor` que `menor`. Cero es parejos. */
  diferenciaUsd: number;
}

/**
 * Cuanto mas saco uno que el otro. Solo tiene sentido entre DOS socios: con
 * uno no hay contra quien, y con tres "la diferencia" deja de ser un numero.
 */
export function diferenciaEntreSocios(
  resumen: ReadonlyArray<ResumenSocio>,
): DiferenciaSocios | null {
  if (resumen.length !== 2) return null;
  const [a, b] = resumen as [ResumenSocio, ResumenSocio];
  const [mayor, menor] = a.netoUsd >= b.netoUsd ? [a, b] : [b, a];
  return { mayor, menor, diferenciaUsd: redondear(mayor.netoUsd - menor.netoUsd) };
}
